import type { ReactNode } from "react";
import { LedgerRow } from "../internal/BaseLedgerRow";
import { HollowDot, KindTag, Money, Quantity } from "../primitives";
import type { LedgerCollapse, LedgerColumn } from "../types";

export interface TemplateLedgerRowProps {
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  /** The labour item name. */
  title: string;
  /**
   * What the item is worth once someone books it. Null when the viewer may
   * not see money.
   */
  targetAmount?: number | null;
  quantity?: { value: number; unitLabel: string } | null;
  tagLabel?: string;
  showValues?: boolean;
  hoverCard?: ReactNode;
  onClick?: () => void;
  "data-testid"?: string;
}

/**
 * An item nobody has booked yet: hollow dot, "Not booked", quiet target value.
 * Never dimmed — a freshly imported plot is the price book, not a problem, and
 * every labour item renders whether or not it has an allocation.
 */
export const TemplateLedgerRow = ({
  columns,
  collapse,
  title,
  targetAmount,
  quantity,
  tagLabel = "Not booked",
  showValues = true,
  hoverCard,
  onClick,
  "data-testid": dataTestId,
}: TemplateLedgerRowProps) => (
  <LedgerRow
    columns={columns}
    collapse={collapse}
    lead={<HollowDot />}
    title={title}
    titleTag={<KindTag>{tagLabel}</KindTag>}
    value={
      quantity ? (
        <Quantity value={quantity.value} unitLabel={quantity.unitLabel} quiet />
      ) : (
        <Money value={targetAmount} quiet />
      )
    }
    showValues={showValues}
    hoverCard={hoverCard}
    onClick={onClick}
    aria-label={`${title}, ${tagLabel}`}
    data-status="NotBooked"
    data-testid={dataTestId}
  />
);
